<template>
    <div>
        <div class="d-flex justify-content-end mb-4">
            <b-button class="btn-new" variant="primary" href="/v2/target/ranges/new">
                {{ $t('form.button.new') }}
            </b-button>
        </div>
        <b-card no-body>
            <b-card-header>
                <i class="icon-list"></i>
                <strong>{{ $t('ranges.ranges') }}</strong>
            </b-card-header>
            <b-card-body>
                <Loader :loader="loader" />
                <div class="d-flex justify-content-end mb-6" style="width: 95%; margin: 0 auto">
                    <b-form-checkbox
                        v-model="companyAllowRangeEdition"
                        @change="toggleRangeEdition(companyAllowRangeEdition)"
                        v-show="!loader"
                        switch
                    >
                        <b>{{ $t('ranges.goalRegistrationAllowsChangeRange') }} </b>
                    </b-form-checkbox>
                </div>
                <v-server-table
                    id="listOfRanges"
                    ref="listOfRanges"
                    class="data-table-default"
                    :columns="data.columns"
                    :options="data.options"
                    :theme="data.theme"
                    v-show="!loader"
                >
                    <template #afterTable>
                        <small class="text-muted lg">
                            <p>{{ $t('ranges.smallerRange') }}</p>
                        </small>
                    </template>
                    <template #beforeTable>
                        <small class="text-muted lg">
                            <p>{{ $t('ranges.largerRange') }}</p>
                        </small>
                    </template>
                    <template #headlight="{ row }">
                        <div class="td-headlight">
                            <Headlight
                                :size="19"
                                :hex="row.headlight.hex"
                                :title="$t(`headlight.${row.headlight.title}`)"
                            />
                        </div>
                    </template>
                    <template #reorder="{ row }">
                        <div class="td-action">
                            <b-link
                                :disabled="isFirst(row.order)"
                                :title="$t('form.button.moveUp')"
                                @click="reorderRanges(ranges, row.id, 'up')"
                            >
                                <i
                                    class="fa fa-arrow-up fa-lg mr-1"
                                    :class="{
                                        'text-secondary': isFirst(row.order),
                                        'text-info': !isFirst(row.order),
                                    }"
                                >
                                </i>
                            </b-link>
                            <b-link
                                :disabled="isLast(row.order)"
                                :title="$t('form.button.moveDown')"
                                @click="reorderRanges(ranges, row.id, 'down')"
                            >
                                <i
                                    class="fa fa-arrow-down fa-lg"
                                    :class="{
                                        'text-secondary': isLast(row.order),
                                        'text-info': !isLast(row.order),
                                    }"
                                >
                                </i>
                            </b-link>
                        </div>
                    </template>
                    <template #target="{ row }">
                        <div class="target">
                            {{ row.target }}
                        </div>
                    </template>
                    <template #multiplier="{ row }">
                        <div class="multiplier">
                            {{ row.multiplier }}
                        </div>
                    </template>
                    <template #isTarget="{ index }">
                        <div class="is-target">
                            <c-switch
                                color="info"
                                variant="pill"
                                size="sm"
                                v-model="isTarget[index - 1].value"
                                @change="
                                    () => {
                                        isTarget = onChangeIsTarget(isTarget, index - 1)
                                    }
                                "
                            />
                        </div>
                    </template>
                    <template #update="{ row }">
                        <div class="td-action">
                            <b-link :href="`/v2/target/ranges/edit/${row.id}`" :title="$t('form.button.edit')">
                                <i class="fa fa-pencil fa-lg text-primary"></i>
                            </b-link>
                        </div>
                    </template>
                    <template #delete="{ row }">
                        <div class="td-action">
                            <b-link :title="$t('form.button.delete')" @click="deleteRange(row.id)">
                                <i class="fa fa-trash fa-lg text-danger"></i>
                            </b-link>
                        </div>
                    </template>
                </v-server-table>
            </b-card-body>
        </b-card>
    </div>
</template>

<script>
import Vue from 'vue'
import Swal from 'sweetalert2'
import { ServerTable } from 'vue-tables-2'
import i18n from '@/i18n'
import api from '@/services/api'
import configDataTable from '@/config/data-table'
import { deleteConfirmationWithLoading } from '@/config/sweet-alert'
import notification, { success, danger } from '@/lib/notification'
import Loader from '@/views/components/loader'
import Headlight from '@/views/components/headlight'
import { findIndex } from 'lodash'
import { Switch as cSwitch } from '@coreui/vue'
import { getUserPermissions } from '@/services/user-permissions'
import { isCompany } from '@/services/companies-custom-functions'

notification.init()
Vue.use(ServerTable)

export default {
    name: 'List',
    components: {
        ServerTable,
        Loader,
        Headlight,
        cSwitch,
    },
    data() {
        const _self = this
        const { options, theme } = configDataTable
        return {
            isGol: isCompany('gol') || isCompany('testegol'),
            ranges: [],
            isTarget: [],
            initialRangeSelected: null,
            loader: false,
            data: {
                columns: ['reorder', 'title', 'headlight', 'target', 'multiplier', 'isTarget', 'update', 'delete'],
                options: {
                    sortable: [],
                    orderBy: {
                        column: 'order',
                        ascending: false,
                    },
                    headings: {
                        title: i18n.t('form.label.title'),
                        isTarget: i18n.t('form.label.isTarget'),
                        target: i18n.t('form.label.target'),
                        multiplier: i18n.t('form.label.multiplier'),
                        headlight: i18n.t('form.label.headlight'),
                        reorder: i18n.t('form.button.reorder'),
                        update: i18n.t('form.button.edit'),
                        delete: i18n.t('form.button.delete'),
                    },
                    requestFunction(data) {
                        _self.loader = true
                        const { limit, page, query, byColumn, orderBy, ascending } = data
                        return api
                            .get('/ranges', {
                                params: {
                                    limit,
                                    page,
                                    query,
                                    byColumn,
                                    orderBy,
                                    ascending,
                                },
                            })
                            .catch((e) => {
                                console.log(e)
                            })
                    },
                    responseAdapter(response) {
                        _self.loader = false
                        const { data, count } = response.data.data
                        _self.ranges = data
                        _self.isTarget = data.map((d) => {
                            const { id, is_target } = d
                            return {
                                id,
                                value: is_target,
                            }
                        })
                        const initialRangeSelected = data.filter((t) => t.is_target)[0]
                        _self.initialRangeSelected = initialRangeSelected ? initialRangeSelected.id : null
                        return {
                            data: data,
                            count,
                        }
                    },
                    ...options,
                },
                ...theme,
            },
        }
    },
    mounted() {
        const { is_admin, is_target_master, is_local_administrator, is_god } = getUserPermissions()
        if (!is_admin && !is_target_master && !is_local_administrator && !is_god) {
            this.$router.push({
                path: '/v2/pages/error/403',
            })
        }

        if (this.isGol) {
            this.data.columns = [
                'reorder',
                'title',
                'headlight',
                'target',
                'multiplier',
                'isTarget',
                'update',
                'delete',
            ]
        } else {
            this.data.columns = ['reorder', 'title', 'headlight', 'target', 'isTarget', 'update', 'delete']
        }
    },
    computed: {
        isFirst() {
            return (position) => {
                const FIRST_POSITION = this.ranges.length
                return position === FIRST_POSITION
            }
        },
        isLast() {
            return (position) => {
                const LAST_POSITION = 1
                return position === LAST_POSITION
            }
        },
        currentUser() {
            return this.$store.state.currentUser.user
        },
        userCompany() {
            return this.currentUser.company
        },
        companyAllowRangeEdition: {
            get: function () {
                return this.userCompany.allow_range_edition
            },
            set: function (newValue) {
                this.userCompany.allow_range_edition = newValue
            },
        },
    },
    methods: {
        onChangeIsTarget(targets, index) {
            const cloneTargets = [...targets]
            const changedTargetId = cloneTargets[index].id
            const changedTargetValue = cloneTargets[index].value
            const updatedTargets = cloneTargets.map((newTarget) => {
                if (changedTargetValue && newTarget.id != changedTargetId) {
                    newTarget.value = false
                }
                return newTarget
            })
            const selectedRange = { ...updatedTargets.filter((t) => t.value)[0] }
            if (selectedRange.value && this.initialRangeSelected != selectedRange.id) {
                this.updateRangesIsTarget(selectedRange.id)
            }
            return updatedTargets
        },
        updateRangesIsTarget(id) {
            api.put(`/ranges/${id}/set-target`)
                .then(() => {
                    this.$refs.listOfRanges.refresh()
                    this.notificationUpdatedSuccessfully()
                })
                .catch((e) => {
                    console.log(e)
                    this.notificationError()
                })
        },
        deleteRangeFromAPI(id) {
            return api
                .delete(`/ranges/${id}`)
                .then(() => {
                    this.$refs.listOfRanges.refresh()
                })
                .catch((e) => {
                    console.log(e)
                })
        },
        updateRangesOrder(newRanges) {
            api.post('/ranges/order', { ranges: newRanges })
                .then(() => {
                    this.$refs.listOfRanges.refresh()
                    this.notificationRangeReorderedSuccessfully()
                })
                .catch((e) => {
                    console.log(e)
                    this.notificationError()
                })
        },
        deleteRange(id) {
            Swal({
                ...deleteConfirmationWithLoading,
                text: i18n.t('notification.goalsLoseRangeReference'),
                preConfirm: () => {
                    return this.deleteRangeFromAPI(id)
                },
            })
                .then((resp) => {
                    const { value } = resp
                    if (value) {
                        this.notificationRangeDeletedSuccessfully()
                    }
                })
                .catch((e) => {
                    console.log(e)
                    this.notificationError()
                })
        },
        notificationRangeDeletedSuccessfully() {
            success(
                i18n.t('notification.deletedSuccessfully', {
                    item: i18n.t('ranges.range'),
                })
            )
        },
        notificationError() {
            danger()
        },
        notificationUpdatedSuccessfully() {
            success(
                i18n.t('notification.updatedSuccessfully', {
                    item: i18n.t('ranges.range'),
                })
            )
        },
        notificationRangeReorderedSuccessfully() {
            success(
                i18n.t('notification.reorderedSuccessfully', {
                    item: i18n.t('ranges.range'),
                })
            )
        },
        mapperRangesToReoder(ranges) {
            return ranges.map((range) => {
                const { id, order } = range
                return {
                    id,
                    order,
                }
            })
        },
        getRangeIndexById(ranges, id) {
            return findIndex(ranges, (range) => range.id === id)
        },
        reorderRanges(ranges, rangeId, direction) {
            let neighbor = null
            let newRanges = this.mapperRangesToReoder(ranges)
            const originalPosition = this.getRangeIndexById(newRanges, rangeId)
            switch (direction) {
                case 'up':
                    if (originalPosition != 0) {
                        neighbor = originalPosition - 1
                        newRanges[originalPosition].order++
                        newRanges[neighbor].order--
                    }
                    break
                case 'down':
                    if (originalPosition != newRanges.length) {
                        neighbor = originalPosition + 1
                        newRanges[originalPosition].order--
                        newRanges[neighbor].order++
                    }
                    break
                default:
                    return false
            }
            this.updateRangesOrder(newRanges)
        },
        toggleRangeEdition(companyAllowRangeEdition) {
            const companyId = this.userCompany.id

            api.put(`companies/${companyId}/toggle-range-edition`, {
                allow_range_edition: !companyAllowRangeEdition,
            })
                .then(() => {
                    success(
                        i18n.t('notification.updatedSuccessfully', {
                            item: i18n.t('targets.target'),
                        })
                    )
                })
                .catch((error) => {
                    console.log(error)
                    danger()
                })
        },
    },
}
</script>

<style lang="scss" scope>
@import '@/scss/VueTableDefault.scss';

#listOfRanges {
    .VueTables__table {
        .target,
        .multiplier {
            text-align: center;
        }
        .is-target,
        .td-action,
        .td-headlight {
            text-align: center;
        }
        th:nth-child(1),
        th:nth-child(3),
        th:nth-child(4),
        th:nth-child(6),
        th:nth-child(7) {
            width: 100px;
        }
    }
}
.btn-new {
    min-width: 80px;
}
.disabled:hover {
    cursor: default;
}
</style>
